import dayjs, { type Dayjs } from 'dayjs';
import { message } from 'antd';

export const EMPTY_PLACEHOLDER = '-';

export const copyWithFeedback = (value?: string | null) => {
  if (!value) return;

  navigator.clipboard.writeText(value);
  message.success('Copiado para área de transferência.');
};

export const isBlank = (value: unknown) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && !value.trim());

export const displayValue = (
  value?: string | null,
  fallback = EMPTY_PLACEHOLDER,
) => {
  if (!value?.trim()) return fallback;
  return value;
};

export const hasResponseKey = (
  source: object | null | undefined,
  key: PropertyKey,
) => Boolean(source) && Object.prototype.hasOwnProperty.call(source, key);

export const readField = <T,>(
  source: object | Array<object | null | undefined> | null | undefined,
  key: PropertyKey,
  format: (value: T) => string = (value) => String(value),
): string => {
  const sources = Array.isArray(source) ? source : [source];

  for (const item of sources) {
    if (!hasResponseKey(item, key)) continue;

    const value = (item as Record<PropertyKey, unknown>)[key];
    if (isBlank(value)) return EMPTY_PLACEHOLDER;

    return format(value as T);
  }

  return EMPTY_PLACEHOLDER;
};

export const formatRateNumber = (value: number, digits = 2) =>
  `${(value * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;

export const formatMonthlyAnnualRate = (
  source: object | null | undefined,
  monthlyKey: PropertyKey,
  annualKey: PropertyKey,
  monthlyDigits = 2,
  annualDigits = 2,
) => {
  if (
    !hasResponseKey(source, monthlyKey) &&
    !hasResponseKey(source, annualKey)
  ) {
    return EMPTY_PLACEHOLDER;
  }

  return `${readField(source, monthlyKey, (value: number) =>
    formatRateNumber(value, monthlyDigits),
  )} a.m. / ${readField(source, annualKey, (value: number) =>
    formatRateNumber(value, annualDigits),
  )} a.a.`;
};

export const formatAccountType = (value: string) =>
  ({
    CACC: 'Conta Corrente',
    CHECKING: 'Corrente',
    SVGS: 'Conta poupança',
    SAVINGS: 'Poupança',
    TRAN: 'Conta de pagamentos',
    PAYMENT: 'Pagamento',
    SLRY: 'Conta salário',
    SALARY: 'Salário',
  }[value] ?? value);

export const formatDatePtBr = (
  value?: string | null,
  fallback = EMPTY_PLACEHOLDER,
) => {
  if (!value) return fallback;

  const date = dayjs(value);
  return date.isValid() ? date.format('DD/MM/YYYY') : fallback;
};

export const formatDateTimePtBr = (
  value?: string | null,
  fallback = EMPTY_PLACEHOLDER,
) => {
  if (!value) return fallback;

  const date = dayjs(value);
  return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : fallback;
};

export const toApiDate = (value?: Dayjs | null) =>
  value?.isValid() ? value.format('YYYY-MM-DD') : undefined;

export const toDayjsDate = (value?: string) => {
  if (!value) return undefined;

  const date = dayjs(value);
  return date.isValid() ? date : undefined;
};

export const stripTaxpayerId = (value?: string | null) =>
  value ? value.replace(/[./\s-]/g, '').toUpperCase() : '';

export const formatTaxpayerId = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 14);

  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

export const displayTaxpayerId = (
  value?: string | null,
  fallback = EMPTY_PLACEHOLDER,
) => (value?.trim() ? formatTaxpayerId(value) : fallback);

export const formatCurrency = (
  value?: number | null,
  fallback = EMPTY_PLACEHOLDER,
) => {
  if (value === undefined || value === null) return fallback;

  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export const getTaxpayerIdLabel = (value?: string | null) => {
  const digits = value?.replace(/\D/g, '') ?? '';
  if (!digits) return 'CPF/CNPJ';
  return digits.length > 11 ? 'CNPJ' : 'CPF';
};

export const formatPhone = (value?: string | null) => {
  if (!value) return '';

  return value
    .replace(/\D+/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .replace(/(\d{4})-(\d)(\d{4})/, '$1$2-$3')
    .replace(/(-\d{4})\d+?$/, '$1');
};
